import {
  ADMIN_APPEAL_OUTCOMES,
  ADMIN_APPEAL_PENALTY_TYPES,
  RESOLVE_APPEAL_BOUNDS,
  type ResolveAppealBody,
} from '@carcheck/shared';
import { z } from 'zod';

/**
 * RHF+Zod schema for the resolve form (AUT-130, architect 🟠 #1). Mirrors
 * the backend `ResolveAppealSchema` field-for-field + bound-for-bound so
 * client validation matches the server 400s. Outcome-first: the four
 * `ADMIN_APPEAL_OUTCOMES` are mutually exclusive; conditional fields apply
 * per outcome via the same refines the api uses.
 *
 * VALIDATION-ONLY (no `.transform`) — mirrors `ForceCancelDialog` /
 * `RejectReportDialog`: the form's field shape (`ResolveFormValues`) stays
 * the RHF source of truth, and the dialog's submit handler projects it into
 * the wire `ResolveAppealBody` via `buildResolveBody`. This sidesteps the
 * RHF transformed-output generic and keeps the validated shape == the
 * input shape.
 *
 * Error messages are i18n KEYS (CLAUDE.md rule #11) — the dialog resolves
 * them via `t()` at render. Bounds come from `RESOLVE_APPEAL_BOUNDS` (the
 * shared const) — never re-typed magic numbers.
 *
 * Money / day fields are kept as STRINGS in the form (a controlled text
 * input can't hold a partial number cleanly). The schema parses them:
 *   - empty string → treated as "not supplied" (the field is optional);
 *   - non-numeric → a `*Invalid` issue;
 *   - out-of-bounds → the matching min/max/range issue.
 *
 * The SPA-only `refundAmount <= orderTotal` clamp (architect 🟠 #2 — the
 * backend has no upper cap and resolve moves real WebPay money) is applied
 * by `makeResolveSchema(orderTotal)`.
 */

const { decisionReasonMin, decisionReasonMax, refundPercentageMax, adminNotesMax } =
  RESOLVE_APPEAL_BOUNDS;

/** The raw form shape (money/days as strings; penalty type optional). */
export interface ResolveFormValues {
  outcome: (typeof ADMIN_APPEAL_OUTCOMES)[number];
  decisionReason: string;
  refundAmount: string;
  refundPercentage: string;
  inspectorPenaltyType: (typeof ADMIN_APPEAL_PENALTY_TYPES)[number] | '';
  inspectorPenaltyDays: string;
  adminNotes: string;
}

/** Parse a string money/day field → number, or `undefined` when blank. NaN preserved. */
const parseOptionalNumber = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  return Number(trimmed);
};

/**
 * Build the resolve Zod schema bound to a specific order total so the
 * `refundAmount <= orderTotal` clamp can be enforced. `orderTotal` is the
 * detail wire's `order.pricingTotal` parsed to a number.
 */
export const makeResolveSchema = (orderTotal: number) =>
  z
    .object({
      outcome: z.enum(ADMIN_APPEAL_OUTCOMES),
      decisionReason: z
        .string()
        .trim()
        .min(decisionReasonMin, 'appeals.resolveDialog.errors.decisionReasonMin')
        .max(decisionReasonMax, 'appeals.resolveDialog.errors.decisionReasonMax'),
      refundAmount: z.string(),
      refundPercentage: z.string(),
      inspectorPenaltyType: z.union([z.enum(ADMIN_APPEAL_PENALTY_TYPES), z.literal('')]),
      inspectorPenaltyDays: z.string(),
      adminNotes: z
        .string()
        .trim()
        .max(adminNotesMax, 'appeals.resolveDialog.errors.adminNotesMax'),
    })
    .superRefine((v, ctx) => {
      const amount = parseOptionalNumber(v.refundAmount);
      const percentage = parseOptionalNumber(v.refundPercentage);
      const days = parseOptionalNumber(v.inspectorPenaltyDays);

      // refundAmount: numeric, >= 0, <= order total (SPA clamp).
      if (amount !== undefined) {
        if (Number.isNaN(amount)) {
          addIssue(ctx, 'refundAmount', 'appeals.resolveDialog.errors.refundAmountInvalid');
        } else if (amount < RESOLVE_APPEAL_BOUNDS.refundAmountMin) {
          addIssue(ctx, 'refundAmount', 'appeals.resolveDialog.errors.refundAmountMin');
        } else if (amount > orderTotal) {
          addIssue(ctx, 'refundAmount', 'appeals.resolveDialog.errors.refundAmountMax');
        }
      }
      // refundPercentage: numeric, 0..100.
      if (percentage !== undefined) {
        if (Number.isNaN(percentage)) {
          addIssue(ctx, 'refundPercentage', 'appeals.resolveDialog.errors.refundPercentageInvalid');
        } else if (
          percentage < RESOLVE_APPEAL_BOUNDS.refundPercentageMin ||
          percentage > refundPercentageMax
        ) {
          addIssue(ctx, 'refundPercentage', 'appeals.resolveDialog.errors.refundPercentageRange');
        }
      }
      // inspectorPenaltyDays: int 1..365 (only meaningful for suspension).
      if (days !== undefined) {
        if (!Number.isInteger(days)) {
          addIssue(ctx, 'inspectorPenaltyDays', 'appeals.resolveDialog.errors.penaltyDaysInt');
        } else if (
          days < RESOLVE_APPEAL_BOUNDS.inspectorPenaltyDaysMin ||
          days > RESOLVE_APPEAL_BOUNDS.inspectorPenaltyDaysMax
        ) {
          addIssue(ctx, 'inspectorPenaltyDays', 'appeals.resolveDialog.errors.penaltyDaysRange');
        }
      }
      // refund_partial requires refundAmount OR refundPercentage (Zod refine
      // mirror — at least one). Surface on refundAmount so the field lights.
      if (v.outcome === 'refund_partial' && amount === undefined && percentage === undefined) {
        addIssue(ctx, 'refundAmount', 'appeals.resolveDialog.errors.refundPartialRequired');
      }
      // inspector_penalty requires inspectorPenaltyType (Zod refine mirror).
      if (v.outcome === 'inspector_penalty' && v.inspectorPenaltyType === '') {
        addIssue(ctx, 'inspectorPenaltyType', 'appeals.resolveDialog.errors.penaltyTypeRequired');
      }
      // suspension requires a day count (the api accepts an undefined days but
      // a suspension with no duration is operator error — block it client-side).
      if (
        v.outcome === 'inspector_penalty' &&
        v.inspectorPenaltyType === 'suspension' &&
        days === undefined
      ) {
        addIssue(ctx, 'inspectorPenaltyDays', 'appeals.resolveDialog.errors.penaltyDaysRequired');
      }
    });

function addIssue(ctx: z.RefinementCtx, field: keyof ResolveFormValues, message: string): void {
  ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
}

/**
 * Project a VALIDATED form into the exact `ResolveAppealBody` the api
 * expects — dropping fields irrelevant to the chosen outcome so the
 * serialised body matches the server's per-outcome expectations:
 *
 *   - refund_full       → outcome + decisionReason (+ optional adminNotes).
 *                         refundAmount is computed server-side; we OMIT it.
 *   - refund_partial    → outcome + decisionReason + refundAmount AND/OR
 *                         refundPercentage (whichever was supplied).
 *   - reject_appeal     → outcome + decisionReason (no money / no penalty).
 *   - inspector_penalty → outcome + decisionReason + inspectorPenaltyType
 *                         (+ inspectorPenaltyDays only for `suspension`).
 *
 * Called from the dialog's submit handler AFTER Zod validation passes, so
 * numeric parses here are known-good.
 */
export const buildResolveBody = (v: ResolveFormValues): ResolveAppealBody => {
  const body: ResolveAppealBody = {
    outcome: v.outcome,
    decisionReason: v.decisionReason.trim(),
  };

  const adminNotes = v.adminNotes.trim();
  if (adminNotes !== '') body.adminNotes = adminNotes;

  if (v.outcome === 'refund_partial') {
    const amount = parseOptionalNumber(v.refundAmount);
    const percentage = parseOptionalNumber(v.refundPercentage);
    if (amount !== undefined) body.refundAmount = amount;
    if (percentage !== undefined) body.refundPercentage = percentage;
  }

  if (v.outcome === 'inspector_penalty' && v.inspectorPenaltyType !== '') {
    body.inspectorPenaltyType = v.inspectorPenaltyType;
    if (v.inspectorPenaltyType === 'suspension') {
      const days = parseOptionalNumber(v.inspectorPenaltyDays);
      if (days !== undefined) body.inspectorPenaltyDays = days;
    }
  }

  return body;
};

/** Default form values for a fresh resolve dialog. */
export const RESOLVE_FORM_DEFAULTS: ResolveFormValues = {
  outcome: 'refund_full',
  decisionReason: '',
  refundAmount: '',
  refundPercentage: '',
  inspectorPenaltyType: '',
  inspectorPenaltyDays: '',
  adminNotes: '',
};
